import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, ListTodo, TrendingUp, Users, AlertTriangle, Check } from 'lucide-react';
import TaskTimeline from '../../components/TaskTimeline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQ_OPTIONS = [
  { value: 'daily',   label: 'Daily' },
  { value: 'weekly',  label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'once',    label: 'One-off' },
];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PRIORITIES = ['low', 'medium', 'high'];
const STATUS_COLOR = {
  pending:     'bg-slate-500/15 text-slate-700 border-slate-500/30',
  in_progress: 'bg-amber-500/15 text-amber-700 border-amber-500/40',
  done:        'bg-emerald-500/15 text-emerald-700 border-emerald-500/40',
};

const EMPTY_TASK = {
  title: '', description: '', frequency: 'daily',
  weekday: 0, day_of_month: 1, start_date: '', due_date: '', due_time: '', priority: 'medium',
  assigned_to_ids: [],
};

export default function ClerkTasksAdmin() {
  const [tasks, setTasks] = useState([]);
  const [clerks, setClerks] = useState([]);
  const [teamKpi, setTeamKpi] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_TASK);
  const [editing, setEditing] = useState(null);
  const [filterClerk, setFilterClerk] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [delayDraft, setDelayDraft] = useState({}); // {task_id: text}

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [tasksRes, clerksRes, kpiRes] = await Promise.all([
        axios.get(`${API_URL}/api/clerk-tasks`, { headers }),
        axios.get(`${API_URL}/api/clerks`, { headers }),
        axios.get(`${API_URL}/api/clerk-workbench/team-kpis`, { headers }),
      ]);
      setTasks(tasksRes.data || []);
      setClerks(clerksRes.data || []);
      setTeamKpi(kpiRes.data?.clerks || []);
    } catch {
      toast.error('Failed to load data');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => { setEditing(null); setForm(EMPTY_TASK); setShowForm(true); };
  const openEdit = (task) => {
    setEditing(task);
    setForm({
      ...EMPTY_TASK, ...task,
      assigned_to_ids: [task.assigned_to_id].filter(Boolean),
    });
    setShowForm(true);
  };

  const toggleClerk = (id) => {
    setForm(f => ({
      ...f,
      assigned_to_ids: f.assigned_to_ids.includes(id)
        ? f.assigned_to_ids.filter(x => x !== id)
        : [...f.assigned_to_ids, id],
    }));
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/clerk-tasks/${editing.id}`, form, { headers });
        toast.success('Task updated');
      } else {
        const res = await axios.post(`${API_URL}/api/clerk-tasks`, form, { headers });
        toast.success(`Task assigned to ${res.data.inserted} clerk(s)`);
      }
      setShowForm(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const del = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/clerk-tasks/${task.id}`, { headers });
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  const saveDelayReason = async (task) => {
    const reason = (delayDraft[task.id] ?? task.delay_reason ?? '').trim();
    try {
      await axios.put(`${API_URL}/api/clerk-tasks/${task.id}`, { delay_reason: reason }, { headers });
      toast.success('Delay reason saved');
      setDelayDraft(d => { const n = { ...d }; delete n[task.id]; return n; });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const filteredTasks = filterClerk === 'all'
    ? tasks
    : tasks.filter(t => t.assigned_to_id === filterClerk);
  const overdueTasks = filteredTasks.filter(t => t.is_overdue);
  const nonOverdue = filteredTasks.filter(t => !t.is_overdue);

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <ListTodo className="h-7 w-7 text-blue-600" />
              Clerk Task Manager
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Assign daily / weekly / monthly tasks to clerks and track their KPIs.</p>
          </div>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="new-task-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New Task
          </Button>
        </div>

        {/* Team KPIs */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-indigo-500" /> Team KPIs — Today
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {teamKpi.length === 0 ? (
              <p className="text-center text-muted-foreground py-5">No clerks found.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Clerk</TableHead>
                    <TableHead className="text-center">Loans</TableHead>
                    <TableHead className="text-center">Drivers</TableHead>
                    <TableHead className="text-center">Statements</TableHead>
                    <TableHead className="text-center">Parts Images</TableHead>
                    <TableHead className="text-center">Stock Loads</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teamKpi.map(c => (
                    <TableRow key={c.id} data-testid={`team-kpi-row-${c.id}`}>
                      <TableCell className="font-medium flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 text-slate-400" />{c.full_name || c.username}
                      </TableCell>
                      <TableCell className="text-center">{c.loans_captured}</TableCell>
                      <TableCell className="text-center">{c.drivers_onboarded}</TableCell>
                      <TableCell className="text-center">{c.statements_uploaded}</TableCell>
                      <TableCell className="text-center">{c.parts_images_captured}</TableCell>
                      <TableCell className="text-center">{c.stock_loaded}</TableCell>
                      <TableCell className="text-center font-bold">
                        <Badge variant={c.total > 0 ? 'default' : 'outline'}>{c.total}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Tasks */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base flex items-center gap-2"><ListTodo className="h-5 w-5" /> Tasks</CardTitle>
            <Select value={filterClerk} onValueChange={setFilterClerk}>
              <SelectTrigger className="w-56 h-8 text-xs" data-testid="filter-clerk-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clerks</SelectItem>
                {clerks.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name || c.username}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="ml-4 mt-2" data-testid="tasks-tabs-list">
                <TabsTrigger value="all" data-testid="tab-all">All Tasks ({nonOverdue.length + overdueTasks.length})</TabsTrigger>
                <TabsTrigger value="overdue" data-testid="tab-overdue" className={overdueTasks.length > 0 ? 'text-rose-600 data-[state=active]:bg-rose-100' : ''}>
                  <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                  Overdue ({overdueTasks.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all">
                {filteredTasks.length === 0 ? (
                  <p className="text-center py-10 text-muted-foreground">
                    <ListTodo className="h-10 w-10 mx-auto mb-2 opacity-40" />
                    No tasks yet. Create one using &quot;New Task&quot;.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Frequency</TableHead>
                        <TableHead>Start → End</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="min-w-[180px]">Timeline / Grade</TableHead>
                        <TableHead>Last Completed</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTasks.map(t => (
                        <TableRow key={t.id} data-testid={`admin-task-row-${t.id}`} className={t.is_overdue ? 'bg-rose-50/60' : ''}>
                          <TableCell className="font-medium">
                            {t.title}
                            {t.is_overdue && (
                              <Badge className="ml-2 bg-rose-500 text-white text-[10px]" data-testid={`overdue-badge-${t.id}`}>
                                OVERDUE
                              </Badge>
                            )}
                            {t.description && <div className="text-xs text-muted-foreground">{t.description}</div>}
                          </TableCell>
                          <TableCell>{t.assignee_name || '—'}</TableCell>
                          <TableCell className="capitalize">
                            {t.frequency}
                            {t.frequency === 'weekly' && <span className="text-xs text-muted-foreground ml-1">({WEEKDAYS[t.weekday ?? 0]})</span>}
                            {t.frequency === 'monthly' && <span className="text-xs text-muted-foreground ml-1">(day {t.day_of_month})</span>}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap" data-testid={`task-dates-${t.id}`}>
                            <div>{t.start_date ? <span className="text-slate-600">{t.start_date}</span> : <span className="text-slate-400">—</span>}</div>
                            <div className={t.is_overdue ? 'text-rose-600 font-semibold' : 'text-slate-500'}>→ {t.due_date || '—'}{t.due_time ? ` ${t.due_time}` : ''}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{t.priority}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`capitalize ${STATUS_COLOR[t.status] || ''}`}>{t.status.replace('_', ' ')}</Badge>
                          </TableCell>
                          <TableCell className="min-w-[180px]">
                            <TaskTimeline task={t} />
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {t.last_completed_at ? new Date(t.last_completed_at).toLocaleString('en-ZA') : '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="icon" variant="ghost" onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => del(t)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>

              <TabsContent value="overdue">
                {overdueTasks.length === 0 ? (
                  <p className="text-center py-10 text-emerald-700" data-testid="no-overdue">
                    <Check className="h-10 w-10 mx-auto mb-2 opacity-60" />
                    No overdue tasks — nice.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Due</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead className="min-w-[300px]">Why delayed?</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {overdueTasks.map(t => (
                        <TableRow key={t.id} data-testid={`overdue-row-${t.id}`} className="bg-rose-50/40">
                          <TableCell className="font-medium">
                            {t.title}
                            {t.description && <div className="text-xs text-muted-foreground">{t.description}</div>}
                          </TableCell>
                          <TableCell>{t.assignee_name || '—'}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap text-rose-600 font-semibold">
                            {t.due_date || '—'}{t.due_time ? ` ${t.due_time}` : ''}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{t.priority}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-start gap-1">
                              <Textarea
                                rows={2}
                                placeholder="Explain the delay (e.g. supplier late, workload, waiting on…)"
                                value={delayDraft[t.id] ?? t.delay_reason ?? ''}
                                onChange={(e) => setDelayDraft(d => ({ ...d, [t.id]: e.target.value }))}
                                className="text-xs"
                                data-testid={`delay-reason-input-${t.id}`}
                              />
                              <Button
                                size="icon"
                                variant="outline"
                                onClick={() => saveDelayReason(t)}
                                data-testid={`delay-reason-save-${t.id}`}
                                title="Save reason"
                              >
                                <Check className="h-4 w-4 text-emerald-600" />
                              </Button>
                            </div>
                            {t.delay_reason_by && t.delay_reason_at && (
                              <div className="text-[10px] text-slate-500 mt-0.5">
                                by {t.delay_reason_by} · {new Date(t.delay_reason_at).toLocaleString('en-ZA')}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="icon" variant="ghost" onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => del(t)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Task form */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Task' : 'New Task'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger data-testid="task-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQ_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.frequency === 'weekly' && (
                <div>
                  <Label>Weekday</Label>
                  <Select value={String(form.weekday)} onValueChange={(v) => setForm({ ...form, weekday: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {form.frequency === 'monthly' && (
                <div>
                  <Label>Day of Month</Label>
                  <Input type="number" min="1" max="31" value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: parseInt(e.target.value) || 1 })} />
                </div>
              )}
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3" data-testid="task-dates-group">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="task-start-date-input" />
              </div>
              <div>
                <Label>End / Due Date</Label>
                <Input type="date" value={form.due_date || ''} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-date-input" />
              </div>
              <div>
                <Label>Due Time <span className="text-xs text-muted-foreground">(SAST · default 17:00)</span></Label>
                <Input type="time" value={form.due_time || ''} onChange={(e) => setForm({ ...form, due_time: e.target.value })} data-testid="task-due-time-input" />
              </div>
            </div>

            {!editing && (
              <div>
                <Label>Assign to (leave all unchecked to broadcast to every clerk)</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mt-2 p-2 border rounded-lg max-h-44 overflow-y-auto">
                  {clerks.length === 0 && <p className="text-xs text-muted-foreground">No clerks yet.</p>}
                  {clerks.map(c => (
                    <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={form.assigned_to_ids.includes(c.id)}
                        onChange={() => toggleClerk(c.id)}
                        data-testid={`assign-${c.username}`}
                      />
                      <span>{c.full_name || c.username}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {form.assigned_to_ids.length === 0 ? 'No clerks selected — task will be assigned to ALL clerks.' : `Will be assigned to ${form.assigned_to_ids.length} clerk(s).`}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-task-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
